import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  MarkerType,
  useReactFlow,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from '@dagrejs/dagre'
import { motion } from 'framer-motion'
import { Badge, Tag } from './ui'
import { BASELINE_CONDITIONS, DEFAULT_GOAL_ID, getPathGenerators } from '../data/layers'
import { OrionFlowNode, type OrionFlowNodeType } from './OrionFlowNode'
import { OrionFlowEdge } from './OrionFlowEdge'
import { useT } from '../lib/i18n'
import { useAutoDemo } from '../lib/auto-demo'
import type { ConditionState, OntoPathNode } from '../types'
import type { Locale } from '../lib/locales'

const nodeTypes = { orion: OrionFlowNode }
const edgeTypes = { orion: OrionFlowEdge }

const ESTIMATED_NODE_WIDTH = 190
const ESTIMATED_NODE_HEIGHT = 52

interface LayoutResult {
  nodes: OrionFlowNodeType[]
  graphHeight: number
  graphWidth: number
}

function format(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key]
    return v === undefined || v === null ? '' : String(v)
  })
}

function layoutGraph(
  rfNodes: OrionFlowNodeType[],
  edges: Edge[],
): LayoutResult {
  const g = new dagre.graphlib.Graph()
  g.setGraph({
    rankdir: 'LR',
    ranksep: 55,
    nodesep: 18,
    edgesep: 12,
    marginx: 12,
    marginy: 12,
  })
  g.setDefaultEdgeLabel(() => ({}))

  rfNodes.forEach((n) => {
    g.setNode(n.id, { width: ESTIMATED_NODE_WIDTH, height: ESTIMATED_NODE_HEIGHT })
  })
  edges.forEach((e) => g.setEdge(e.source, e.target))

  dagre.layout(g)

  const { width: graphWidth, height: graphHeight } = g.graph() as {
    width: number
    height: number
  }

  const laidNodes = rfNodes.map((n) => {
    const pos = g.node(n.id)
    return {
      ...n,
      position: {
        x: pos.x - ESTIMATED_NODE_WIDTH / 2,
        y: pos.y - ESTIMATED_NODE_HEIGHT / 2,
      },
    }
  })

  return { nodes: laidNodes, graphHeight, graphWidth }
}

const STAGGER_DEPTH = 0.18

const edgeStyleIdle: React.CSSProperties = {
  stroke: 'oklch(75% 0.16 65 / 0.7)',
  strokeWidth: 1.5,
  strokeDasharray: '4 4',
}
const edgeStyleOn: React.CSSProperties = {
  stroke: 'oklch(75% 0.16 65 / 1)',
  strokeWidth: 2.2,
}
const edgeStyleOff: React.CSSProperties = {
  stroke: 'oklch(58% 0.03 250 / 0.2)',
  strokeWidth: 1,
  strokeDasharray: '3 4',
}
const markerEndIdle = {
  type: MarkerType.ArrowClosed,
  color: 'oklch(75% 0.16 65)',
  width: 16,
  height: 16,
}
const markerEndOn = {
  type: MarkerType.ArrowClosed,
  color: 'oklch(75% 0.16 65)',
  width: 18,
  height: 18,
}
const markerEndOff = {
  type: MarkerType.ArrowClosed,
  color: 'oklch(58% 0.03 250 / 0.4)',
  width: 14,
  height: 14,
}

function computeAncestors(path: OntoPathNode[], rootId: string): Set<string> {
  const byId: Record<string, OntoPathNode> = {}
  path.forEach((p) => {
    byId[p.id] = p
  })
  const ancestors = new Set<string>()
  const stack = [rootId]
  while (stack.length > 0) {
    const id = stack.pop()!
    if (ancestors.has(id)) continue
    ancestors.add(id)
    const node = byId[id]
    node?.parents?.forEach((pid) => stack.push(pid))
  }
  return ancestors
}

function computeDepths(path: OntoPathNode[]): Record<string, number> {
  const byId: Record<string, OntoPathNode> = {}
  path.forEach((p) => {
    byId[p.id] = p
  })
  const depths: Record<string, number> = {}
  function dfs(id: string): number {
    if (depths[id] !== undefined) return depths[id]
    const n = byId[id]
    if (!n || !n.parents || n.parents.length === 0) {
      depths[id] = 0
      return 0
    }
    const d = Math.max(...n.parents.map((p) => dfs(p))) + 1
    depths[id] = d
    return d
  }
  path.forEach((n) => dfs(n.id))
  return depths
}

export function L3Demo() {
  return (
    <ReactFlowProvider>
      <L3DemoInner />
    </ReactFlowProvider>
  )
}

function L3DemoInner() {
  const t = useT()
  const l3 = t.l3
  const [goalId, setGoalId] = useState<string>(DEFAULT_GOAL_ID)
  const [conds, setConds] = useState<ConditionState>(BASELINE_CONDITIONS)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const reactFlow = useReactFlow()
  const autoDemo = useAutoDemo()

  // Auto-demo: react to step actions.
  useEffect(() => {
    const action = autoDemo.currentStep?.action
    if (!action) return
    switch (action.target) {
      case 'l3-goal':
        setGoalId(action.value)
        setExpandedId(null)
        setSelectedId(null)
        break
      case 'l3-conditions':
        setConds(action.value)
        break
      case 'l3-node':
        setExpandedId(action.value)
        setSelectedId(action.value)
        break
      case 'l3-reset':
        setGoalId(DEFAULT_GOAL_ID)
        setConds(BASELINE_CONDITIONS)
        setExpandedId(null)
        setSelectedId(null)
        break
      default:
        break
    }
  }, [autoDemo.currentStep])

  const acidic = conds.H2S_pct >= 0.005
  const highCl = conds.Cl_mg_L > 10000
  const highVP = conds.vapor_pressure_ratio > 0.3
  const constraintsCount = [acidic, highCl, highVP].filter(Boolean).length

  const generators = useMemo(() => getPathGenerators(t), [t])
  const path = useMemo(() => generators[goalId](conds), [generators, goalId, conds])
  const currentGoal = l3.goals.find((g) => g.id === goalId)

  const onToggleNode = useCallback((id: string) => {
    setExpandedId((curr) => (curr === id ? null : id))
    setSelectedId((curr) => (curr === id ? null : id))
  }, [])

  const onClearAttribution = useCallback(() => {
    setSelectedId(null)
    setExpandedId(null)
  }, [])

  const ancestorSet = useMemo(() => {
    if (!selectedId) return null
    return computeAncestors(path, selectedId)
  }, [path, selectedId])

  const depths = useMemo(() => computeDepths(path), [path])

  const { nodes, edges, boardHeight } = useMemo(() => {
    const rfNodes: OrionFlowNodeType[] = path.map((p) => {
      const depth = depths[p.id] ?? 0
      const attribution: 'on' | 'off' | 'idle' = !ancestorSet
        ? 'idle'
        : ancestorSet.has(p.id)
          ? 'on'
          : 'off'
      return {
        id: p.id,
        type: 'orion',
        position: { x: 0, y: 0 },
        data: {
          kind: p.kind,
          kindLabel: l3.kindLabels[p.kind],
          label: p.label,
          detail: p.detail,
          citation: p.citation,
          expanded: expandedId === p.id,
          onToggle: () => onToggleNode(p.id),
          appearKey: `${goalId}::${p.id}::${ancestorSet ? 'attr' : 'idle'}`,
          appearDelay: ancestorSet ? 0 : depth * STAGGER_DEPTH,
          attribution,
        },
      }
    })

    const rfEdges: Edge[] = []
    path.forEach((p) => {
      const childDepth = depths[p.id] ?? 0
      ;(p.parents ?? []).forEach((pid) => {
        const edgeAttr: 'on' | 'off' | 'idle' = !ancestorSet
          ? 'idle'
          : ancestorSet.has(pid) && ancestorSet.has(p.id)
            ? 'on'
            : 'off'
        const style =
          edgeAttr === 'on' ? edgeStyleOn : edgeAttr === 'off' ? edgeStyleOff : edgeStyleIdle
        const marker =
          edgeAttr === 'on' ? markerEndOn : edgeAttr === 'off' ? markerEndOff : markerEndIdle
        rfEdges.push({
          id: `${pid}->${p.id}`,
          source: pid,
          target: p.id,
          type: 'orion',
          animated: false,
          style,
          markerEnd: marker,
          data: {
            appearKey: `${goalId}::${pid}->${p.id}::${ancestorSet ? 'attr' : 'idle'}`,
            appearDelay: ancestorSet ? 0 : Math.max(0, childDepth * STAGGER_DEPTH - 0.05),
          },
        })
      })
    })

    const layout = layoutGraph(rfNodes, rfEdges)
    const computedHeight = Math.max(200, Math.min(320, Math.round(layout.graphHeight + 60)))
    return { nodes: layout.nodes, edges: rfEdges, boardHeight: computedHeight }
  }, [path, expandedId, onToggleNode, ancestorSet, depths, goalId, l3.kindLabels])

  const selectedNode = selectedId ? path.find((p) => p.id === selectedId) : null

  const topologyKey = useMemo(
    () =>
      path
        .map((n) => n.id)
        .sort()
        .join('|'),
    [path],
  )

  useEffect(() => {
    const ti = window.setTimeout(() => {
      reactFlow.fitView({ padding: 0.08, duration: 400, maxZoom: 1.1, minZoom: 0.55 })
    }, 30)
    return () => window.clearTimeout(ti)
  }, [goalId, topologyKey, reactFlow])

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(420px,auto)] gap-5 lg:gap-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                {l3.selectGoalLabel}
              </span>
              <span className="text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
                {l3.selectGoalHint}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {l3.goals.map((g) => {
                const active = g.id === goalId
                return (
                  <button
                    key={g.id}
                    onClick={() => {
                      setGoalId(g.id)
                      setExpandedId(null)
                      setSelectedId(null)
                    }}
                    className={`text-left px-3 py-2 rounded-[var(--radius-md)] transition-all duration-150 ease-[var(--ease-orion)] ${
                      active
                        ? 'bg-[color:var(--color-orion-amber)]/15 border border-[color:var(--color-orion-amber)]/60'
                        : 'border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                    }`}
                  >
                    <div
                      className={`text-[length:var(--text-small)] font-medium ${
                        active ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-text)]'
                      }`}
                    >
                      {g.label}
                    </div>
                    <div className="text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] mt-0.5 leading-snug">
                      {g.caption}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div
            data-demo-target="l3-sliders"
            className="lg:border-l lg:border-[color:var(--color-orion-surface)]/40 lg:pl-8 min-w-0"
          >
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                {l3.adjustConditionLabel}
              </span>
              <span className="text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
                {l3.adjustConditionHint}
              </span>
              <Badge tone={constraintsCount === 0 ? 'pass' : 'warn'}>
                {format(l3.constraintsBadgeTpl, { count: constraintsCount })}
              </Badge>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <SliderField
                label={l3.slider.h2sLabel}
                value={conds.H2S_pct}
                min={0}
                max={20}
                step={0.1}
                format={(v) => v.toFixed(1)}
                hot={acidic}
                hint={acidic ? l3.slider.h2sHintHot : l3.slider.h2sHintCold}
                onChange={(v) => setConds((s) => ({ ...s, H2S_pct: v }))}
              />
              <SliderField
                label={l3.slider.clLabel}
                value={conds.Cl_mg_L}
                min={0}
                max={50000}
                step={100}
                format={(v) => v.toLocaleString()}
                hot={highCl}
                hint={highCl ? l3.slider.clHintHot : l3.slider.clHintCold}
                onChange={(v) => setConds((s) => ({ ...s, Cl_mg_L: v }))}
              />
              <SliderField
                label={l3.slider.vpLabel}
                value={conds.vapor_pressure_ratio}
                min={0}
                max={1}
                step={0.01}
                format={(v) => v.toFixed(2)}
                hot={highVP}
                hint={highVP ? l3.slider.vpHintHot : l3.slider.vpHintCold}
                onChange={(v) => setConds((s) => ({ ...s, vapor_pressure_ratio: v }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Whiteboard */}
      <div
        data-demo-target="l3-whiteboard"
        className="surface-night border-faint rounded-[var(--radius-lg)] overflow-hidden flex flex-col"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-[color:var(--color-orion-surface)]/40">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] uppercase tracking-widest">
              {l3.whiteboardTitle}
            </span>
            {currentGoal && (
              <Tag kind="rule" className="text-[length:var(--text-micro)]">
                {currentGoal.label}
              </Tag>
            )}
            <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
              {format(l3.nodesEdgesTpl, { n: nodes.length, e: edges.length })}
            </span>
          </div>
          {selectedNode ? (
            <div className="flex items-center gap-2 mono text-[length:var(--text-micro)] uppercase tracking-widest">
              <Badge tone="warn" dot>
                {l3.attributionLabel}
              </Badge>
              <span className="text-[color:var(--color-orion-text)] normal-case">
                {selectedNode.label}
              </span>
              <button
                onClick={onClearAttribution}
                className="px-2 py-0.5 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-deep)]/40 transition-colors"
              >
                {l3.attributionClear}
              </button>
            </div>
          ) : (
            <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
              {l3.attributionHint}
            </span>
          )}
        </div>

        <div className="relative bg-[color:var(--color-orion-deep)]/30 transition-[height] duration-300" style={{ height: boardHeight }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.08, maxZoom: 1.1, minZoom: 0.55 }}
            proOptions={{ hideAttribution: true }}
            panOnDrag={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={false}
            minZoom={0.3}
            maxZoom={2.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            defaultEdgeOptions={{ type: 'orion' }}
            onPaneClick={onClearAttribution}
            onNodeClick={(_, node) => onToggleNode(node.id)}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1}
              color="oklch(58% 0.03 250 / 0.32)"
            />
          </ReactFlow>
          <FlowControls />
        </div>

        <div className="px-4 sm:px-5 py-3 border-t border-[color:var(--color-orion-surface)]/40 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[length:var(--text-small)] text-[color:var(--color-orion-haze)]">
            {l3.bottomCaption}
          </span>
          <button className="px-3 py-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium text-[length:var(--text-small)] hover:bg-[color:var(--color-orion-amber-dim)] transition-colors">
            {l3.pinButton}
          </button>
        </div>
      </div>

      <AdvantagesGrid l3={l3} />
    </div>
  )
}

function AdvantagesGrid({ l3 }: { l3: Locale['l3'] }) {
  return (
    <div data-demo-target="l3-advantages">
      <div className="flex items-center gap-2 mb-3">
        <span className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
          {l3.advantagesTitle}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {l3.advantages.map((adv, i) => (
          <motion.div
            key={adv.title}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: 0.05 * i, duration: 0.35 }}
            className="surface-night border-faint rounded-[var(--radius-md)] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-amber)] uppercase tracking-widest">
                0{i + 1}
              </span>
              <span className="text-[color:var(--color-orion-text)] font-medium">{adv.title}</span>
            </div>
            <p className="text-[length:var(--text-small)] text-[color:var(--color-orion-haze)] leading-relaxed">
              {adv.body}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function FlowControls() {
  const rf = useReactFlow()
  const btnClass =
    'w-8 h-8 flex items-center justify-center rounded-[var(--radius-sm)] mono text-[14px] ' +
    'bg-[color:var(--color-orion-night)]/90 border-faint backdrop-blur-sm ' +
    'text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-amber)]/20 ' +
    'hover:border-[color:var(--color-orion-amber)]/60 transition-colors'
  return (
    <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => rf.zoomIn({ duration: 200 })}
        aria-label="zoom in"
        title="zoom in"
        className={btnClass}
      >
        +
      </button>
      <button
        type="button"
        onClick={() => rf.zoomOut({ duration: 200 })}
        aria-label="zoom out"
        title="zoom out"
        className={btnClass}
      >
        −
      </button>
      <button
        type="button"
        onClick={() => rf.fitView({ padding: 0.08, duration: 400, maxZoom: 1.1, minZoom: 0.55 })}
        aria-label="fit view"
        title="fit view"
        className={btnClass}
      >
        ⤢
      </button>
    </div>
  )
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  format,
  hot,
  hint,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  format: (v: number) => string
  hot: boolean
  hint: string
  onChange: (v: number) => void
}) {
  return (
    <label className="block min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1.5 whitespace-nowrap">
        <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] uppercase tracking-widest">
          {label}
        </span>
        <span
          className={`mono text-[length:var(--text-small)] ${
            hot ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-text)]'
          }`}
        >
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[color:var(--color-orion-amber)]"
      />
      <div
        className={`text-[length:var(--text-micro)] mt-1 leading-snug ${
          hot ? 'text-[color:var(--color-orion-warn)]' : 'text-[color:var(--color-orion-haze)]'
        }`}
      >
        {hint}
      </div>
    </label>
  )
}
